import React, { useEffect, useState, useCallback } from 'react';
import { Search, Eye, X, UserCheck, UserX } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

function UserDetailModal({ user, onClose, onUpdated }: { user: any; onClose: () => void; onUpdated: () => void }) {
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.is_active !== false);
  const [loading, setLoading] = useState(false);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}`, { role, is_active: isActive });
      toast.success('User updated!');
      onUpdated();
      onClose();
    } catch { toast.error('Failed to update user'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white w-full max-w-xl rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">User Details</h2>
          <button onClick={onClose}><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
            <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center text-white text-xl font-bold">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-base font-semibold text-gray-900">{user.name}</p>
              <p className="text-sm text-gray-600">{user.email}</p>
              {user.phone && <p className="text-sm text-gray-500">{user.phone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</p>
              <p className="text-gray-900 mt-1">{new Date(user.created_at).toLocaleDateString('en-PK')}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Orders</p>
              <p className="text-gray-900 mt-1">{user.orders?.length || 0}</p>
            </div>
            {user.city && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">City</p>
                <p className="text-gray-900 mt-1">{user.city}</p>
              </div>
            )}
          </div>

          {user.orders?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Orders</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {user.orders.slice(0, 5).map((order: any) => (
                  <div key={order.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                    <span className="font-medium text-gray-900">{order.order_number}</span>
                    <span className="text-gray-600">Rs.{Number(order.total).toLocaleString()}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${
                      order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                      order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>{order.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Role</label>
              <select value={role} onChange={e => setRole(e.target.value)} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm text-gray-700">Account Active</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-sm font-medium hover:border-gray-900 transition-colors">Cancel</button>
            <button onClick={handleUpdate} disabled={loading} className="px-8 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-60">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const fetchUsers = useCallback((page = 1) => {
    setLoading(true);
    const params: any = { page };
    if (search) params.search = search;
    if (roleFilter) params.role = roleFilter;
    api.get('/admin/users', { params })
      .then(r => {
        setUsers(r.data.data || r.data);
        if (r.data.meta) setPagination({ current_page: r.data.meta.current_page, last_page: r.data.meta.last_page, total: r.data.meta.total });
        else setPagination({ current_page: r.data.current_page || 1, last_page: r.data.last_page || 1, total: r.data.total || 0 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, roleFilter]);

  useEffect(() => { fetchUsers(1); }, [roleFilter]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <p className="text-sm text-gray-500 mt-1">{pagination.total} total users</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchUsers(1)} placeholder="Search by name, email, phone..." className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-gray-900 bg-white">
          <option value="">All Roles</option>
          <option value="customer">Customers</option>
          <option value="admin">Admins</option>
        </select>
        <button onClick={() => fetchUsers(1)} className="px-4 py-2 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors">Search</button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Orders</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? Array(8).fill(null).map((_, i) => (
              <tr key={i}><td colSpan={7} className="px-6 py-4"><div className="h-8 bg-gray-100 animate-pulse rounded" /></td></tr>
            )) : users.map(user => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold text-sm flex-shrink-0">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{user.phone || '—'}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>{user.role}</span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{user.orders_count ?? user.orders?.length ?? 0}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {user.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-500">{new Date(user.created_at).toLocaleDateString('en-PK')}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setSelectedUser(user)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagination.last_page > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
              <button key={page} onClick={() => fetchUsers(page)} className={`w-9 h-9 text-xs font-medium border ${page === pagination.current_page ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-900'}`}>{page}</button>
            ))}
          </div>
        )}
      </div>

      {selectedUser && <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} onUpdated={() => fetchUsers(pagination.current_page)} />}
    </div>
  );
}
